import * as React from 'react'

import { navigate } from "gatsby";
import L, { IconOptions } from "leaflet";
import _ from "lodash";
import { MutableRefObject, Dispatch, SetStateAction, useRef, useEffect, useState, useCallback, useContext } from "react";
import { MarkerInfo } from "../../components/search/map/map";
import { monthSlugs } from "../../services/centralStore";
import getLocationPath from "../../services/getLocationPath";
import isTouchScreen from "../../services/isTouchScreen";
import { generateCircles, generateImage, closeAllMarkers, setCircleHoverStyle, setImageHoverStyle } from "../../services/mapUtils";
import { WeatherSearchResultRow, WeatherSearchResultRowBasic, WeatherSearchResultRowComplete } from "../../services/weatherSearch";
import { setContinentRefresher } from "../../store/weatherSearchSlice";
import { useAppDispatch } from "../useAppDispatch";
import { useAppSelector } from "../useAppSelector";

import * as ReactDOMServer from 'react-dom/server';
import { generateTooltip, MapInfoBox } from '../../components/search/map/info-box';
import { TemperatureUnitContext } from "../../components/layout/layout";
import { WeatherSearchForm } from "../../components/search/forms/weather-search";
import ShareButton from "react-share/lib/ShareButton";

const hiddenMarkers = {
    5: ["saint-martin", "anguilla", "saint-barthelemy"],
    6: ["saint-martin", "anguilla", "saint-barthelemy"],
    7: ["saint-martin", "anguilla", "saint-barthelemy", "saint-kitts-and-nevis", "curacao", "saint-lucia", "bahrain"]
}

interface UseMapMarkersProps {
    mapRef: MutableRefObject<L.Map>;
    fromMainSearchForm: boolean;
    mapInitialized: boolean;
    currentMarkers: MutableRefObject<{ [key: string]: MarkerInfo }>;
    isExtended: boolean;

}


export const useMapMarkers = ({ mapRef, fromMainSearchForm, mapInitialized, currentMarkers, isExtended }: UseMapMarkersProps) => {
    const dispatch = useAppDispatch();
    const [unit] = useContext(TemperatureUnitContext);

    const continent = useAppSelector(state => {
        return state.weatherSearch.searchProperties.continent.val as string
    });
    const month = useAppSelector(state => {
        return state.weatherSearch.searchProperties.month.val as string
    });
    const zoom = useAppSelector(state => {
        return state.weatherSearch.zoom
    });
    const results = useAppSelector(state => {
        return fromMainSearchForm ? state.weatherSearch.searchResultsExtended : state.weatherSearch.searchResults;
    });
    const filteredResults = useAppSelector(state => {
        return fromMainSearchForm ? state.weatherSearch.searchResultsForCards : undefined;
    });
    const inInteraction = useAppSelector(state => {
        return state.weatherSearch.inInteraction;
    });
    const hoveredRegion = useAppSelector(state => {
        return state.weatherSearch.hoveredRegion;
    });

    const monthRef = useRef<string>(month);
    const renderedResultsRef = useRef<WeatherSearchResultRowBasic[]>();
    const renderedFilteredResultsRef = useRef<WeatherSearchResultRowComplete[]>();
    const renderedZoomRef = useRef<number>();
    const renderedInInteractionRef = useRef<boolean>();
    const lastLastHoveredMarker = useRef<MarkerInfo>();





    const markerClick = useCallback((marker: L.Marker | L.CircleMarker, result: WeatherSearchResultRowComplete, ignoreTouchScreen?: boolean) => {
        if (ignoreTouchScreen && isTouchScreen()) return;
        const monthIdx = monthRef.current == 'all' ? 12 : monthSlugs.indexOf(monthRef.current);
        const slug = getLocationPath({ location: result, monthIndex: monthIdx, isCountryInstance: false });
        navigate(slug);
    }, []);


    // hovering connection
    useEffect(() => {
        if (!currentMarkers) return;
        let markerInfos: [MarkerInfo, boolean][] = [];
        // no matter what we need to clear previously hovered marker
        if (lastLastHoveredMarker.current) {
            markerInfos.push([lastLastHoveredMarker.current, false]);
            lastLastHoveredMarker.current = undefined;
        }
        if (hoveredRegion && results) {
            const result = results.find(r => (r as WeatherSearchResultRowComplete).slug == hoveredRegion);
            if (result) {
                const markerInfo = currentMarkers.current[result.id];
                markerInfos.push([markerInfo, true])
                lastLastHoveredMarker.current = markerInfo;
            }
        }
        for (let markerInfoObject of markerInfos) {
            if (markerInfoObject[0].marker) {
                setCircleHoverStyle(markerInfoObject[0].marker, markerInfoObject[1]);
            }
            if (markerInfoObject[0].image) {
                setImageHoverStyle(markerInfoObject[0].image, markerInfoObject[1]);
            }
        }

    }, [hoveredRegion])

    useEffect(() => {
        monthRef.current = month;
    }, [month])



    // generate markers (on results update)
    useEffect(() => {

        if (!mapInitialized || !results) return;
        if (!fromMainSearchForm && continent == 'all' && !inInteraction) {
            dispatch(setContinentRefresher(Math.random().toString(16)));
        }
        const isTouch = isTouchScreen() as boolean;
        const size = 40 + (zoom - 5) * 12;

        const regenerateMarkers = !renderedResultsRef.current || renderedResultsRef.current != results || (zoom < 5 && renderedZoomRef.current >= 5) || (!inInteraction && renderedInInteractionRef.current);
        const resizeImages = zoom != renderedZoomRef.current && zoom >= 5
        // run only with new results and when zoom jums back to visible range
        if (regenerateMarkers || resizeImages) {
            const newIds = results.map(r => r.id);
            const presentIds = Object.keys(currentMarkers.current);
            const idsToAdd = _.difference(newIds, presentIds);
            if (regenerateMarkers) {

                const idsToRemove = _.difference(presentIds, newIds);
                // remove current markers image/circle
                for (let id of idsToRemove) {
                    const layers = currentMarkers.current[id];
                    const layersToRemove = [layers.image, layers.marker, ...layers.circles];
                    for (let layer of layersToRemove) {
                        if (layer) {
                            layer.remove();
                        }
                    }
                    delete currentMarkers.current[id];
                }
            }
            for (let result of results) {
                let currentMarkerInfo: MarkerInfo;
                const resultRowComplete = result as WeatherSearchResultRowComplete;
                // add new circles
                if (idsToAdd.includes(result.id)) {
                    const { mainCircle, addedCircles } = generateCircles({ size: 1, centroid: result.centroid, map: mapRef.current });
                    if (fromMainSearchForm) {
                        mainCircle.on('click', () => {
                            markerClick(mainCircle, resultRowComplete, true);
                        });
                    }
                    currentMarkerInfo = {
                        marker: mainCircle,
                        circles: addedCircles,
                        generatedByInteraction: false
                    }
                    currentMarkers.current[result.id] = currentMarkerInfo;
                    if (fromMainSearchForm) {
                        generateTooltip({ result: resultRowComplete, toBeOpened: false, unit, marker: mainCircle, isForImage: false, isTouch, clickHandler: (marker) => { markerClick(marker, resultRowComplete) } });
                    }

                }
                // just regenerate tooltip
                else {
                    currentMarkerInfo = currentMarkers.current[result.id];
                    if (!inInteraction && regenerateMarkers && fromMainSearchForm) {
                        generateTooltip({ result: resultRowComplete, toBeOpened: false, unit, marker: currentMarkerInfo.marker, isForImage: false, isTouch, clickHandler: (marker) => { markerClick(marker, resultRowComplete) } });
                    }

                }
                // resize existing images
                if (currentMarkerInfo?.image && resizeImages) {
                    const iconOptions = currentMarkerInfo?.image.options.icon.options as IconOptions;
                    const toBeHidden = hiddenMarkers[zoom].includes(resultRowComplete.slug);

                    const newIcon = L.icon({
                        ...iconOptions,
                        tooltipAnchor: toBeHidden ? [10000, 10000] : [0, 0],
                        iconSize: toBeHidden ? [0, 0] : [size, size]
                    })
                    currentMarkerInfo.image.setIcon(newIcon);
                }
                // in case we have existing images and something changed, regenerate images tooltips
                if (currentMarkerInfo?.image && ((zoom >= 5 && !inInteraction && regenerateMarkers) || zoom == 7 || renderedZoomRef.current == 7)) {
                    generateTooltip({ result: result as WeatherSearchResultRowComplete, toBeOpened: zoom == 7, unit, marker: currentMarkerInfo.image, isForImage: true, isTouch, clickHandler: (marker) => { markerClick(marker, resultRowComplete) } });
                }
            }
        }

        // clear images
        if (zoom < 5 && renderedZoomRef.current >= 5 && fromMainSearchForm) {
            for (let key in currentMarkers.current) {
                const markerInfo = currentMarkers.current[key];
                if (markerInfo.image) {
                    markerInfo.image.remove();
                    delete markerInfo.image;
                }
            }
        }
        // add new and check current images
        else if (zoom >= 5 && fromMainSearchForm && filteredResults && filteredResults != renderedFilteredResultsRef.current) {
            for (let result of filteredResults) {
                const resultRowComplete = result as WeatherSearchResultRowComplete;
                const currentMarkerInfo = currentMarkers.current[result.id];

                if (!currentMarkerInfo?.image) {
                    const url = `/thumbs-renamed/${(result as WeatherSearchResultRowComplete).mainImgData.url.replace('.jpg', '.png')}`;
                    const toBeHidden = hiddenMarkers[zoom].includes(resultRowComplete.slug);
                    const marker = generateImage({ centroid: result.centroid, map: mapRef.current, url, size, toBeHidden });

                    generateTooltip({ result: result as WeatherSearchResultRowComplete, toBeOpened: zoom == 7, unit, marker: marker, isForImage: true, isTouch, clickHandler: (marker) => { markerClick(marker, resultRowComplete) } });

                    marker.on('click', () => {
                        markerClick(marker, result as WeatherSearchResultRowComplete, true);
                    });
                    currentMarkerInfo.image = marker;
                    if (currentMarkerInfo.marker) {
                        currentMarkerInfo.marker.unbindTooltip();
                    }
                }
            }
        }
        renderedResultsRef.current = results;
        renderedFilteredResultsRef.current = filteredResults;
        renderedZoomRef.current = zoom;
        renderedInInteractionRef.current = inInteraction;

    }, [results, filteredResults, mapInitialized, zoom, month, inInteraction]);




}

